import { z, ZodError } from 'zod';

/** Metadata describing the song associated with a chart. */
export const songMetadataSchema = z.object({
  title: z.string(),
  artist: z.string(),
  bpm: z.number(),
});
export type SongMetadata = z.infer<typeof songMetadataSchema>;

/** Information about the audio track for a song. */
export const audioSchema = z.object({
  /** Filename or URL of the audio resource. */
  file: z.string(),
  /** Optional global timing offset applied to all charts in seconds. */
  offset: z.number().optional(),
});
export type Audio = z.infer<typeof audioSchema>;

export const themeSchema = z.enum(['ddr', 'circle', 'guitarhero']);
export type Theme = z.infer<typeof themeSchema>;

export const ddrLaneSchema = z.enum(['left', 'down', 'up', 'right']);
export const ddrNoteSchema = z.object({
  time: z.number(),
  lane: ddrLaneSchema,
});
export type DDRLane = z.infer<typeof ddrLaneSchema>;
export type DDRNote = z.infer<typeof ddrNoteSchema>;

export const circleNoteSchema = z.object({
  time: z.number(),
  /** Normalized X coordinate in 9:16 space. */
  x: z.number(),
  /** Normalized Y coordinate in 9:16 space. */
  y: z.number(),
});
export type CircleNote = z.infer<typeof circleNoteSchema>;

/** 2D position in normalized 9:16 space. */
export const positionSchema = z.object({ x: z.number(), y: z.number() });
export type Position = z.infer<typeof positionSchema>;

/** Simple circle hit object. */
export const hitCircleSchema = z.object({
  type: z.literal('circle'),
  /** Start time in milliseconds relative to audio start. */
  tStart: z.number(),
  /** Hit circles do not have an end time, but field is optional for uniformity. */
  tEnd: z.number().optional(),
  /** Normalized position. */
  pos: positionSchema,
  /** Approach rate on a 0-10 scale. */
  approachRate: z.number(),
});
export type HitCircle = z.infer<typeof hitCircleSchema>;

/** Slider object with a defined end time. */
export const sliderSchema = z.object({
  type: z.literal('slider'),
  /** Start time in milliseconds relative to audio start. */
  tStart: z.number(),
  /** End time in milliseconds relative to audio start. */
  tEnd: z.number(),
  /** Normalized position. */
  pos: positionSchema,
  /** Approach rate on a 0-10 scale. */
  approachRate: z.number(),
  /** Optional encoded path commands for the slider. */
  path: z.array(z.string()).optional(),
  /** Pre-smoothed polyline points for the slider path. */
  points: z.array(positionSchema).optional(),
  /** Number of repeats for the slider. */
  repeats: z.number().optional(),
  /** Slider length in pixels relative to the circle playfield. */
  pixelLength: z.number().optional(),
});
export type Slider = z.infer<typeof sliderSchema>;

/** Spinner object with a defined end time. */
export const spinnerSchema = z.object({
  type: z.literal('spinner'),
  /** Start time in milliseconds relative to audio start. */
  tStart: z.number(),
  /** End time in milliseconds relative to audio start. */
  tEnd: z.number(),
  /** Normalized position. */
  pos: positionSchema,
  /** Approach rate on a 0-10 scale. */
  approachRate: z.number(),
});
export type Spinner = z.infer<typeof spinnerSchema>;

export const guitarHeroLaneSchema = z.enum([
  'green',
  'red',
  'yellow',
  'blue',
  'orange',
]);
export const guitarHeroNoteSchema = z.object({
  time: z.number(),
  lane: guitarHeroLaneSchema,
});
export type GuitarHeroLane = z.infer<typeof guitarHeroLaneSchema>;
export type GuitarHeroNote = z.infer<typeof guitarHeroNoteSchema>;

export const noteEventSchema = z.union([
  ddrNoteSchema,
  circleNoteSchema,
  guitarHeroNoteSchema,
  hitCircleSchema,
  sliderSchema,
  spinnerSchema,
]);
export type NoteEvent = z.infer<typeof noteEventSchema>;

export const difficultyNameSchema = z.string();
export type DifficultyName = z.infer<typeof difficultyNameSchema>;

export const difficultyMetadataSchema = z.object({
  /** Difficulty label, e.g. 'easy' or 'hard'. */
  name: difficultyNameSchema,
  /** Optional timing offset applied to all notes in seconds. */
  offset: z.number().optional(),
});
export type DifficultyMetadata = z.infer<typeof difficultyMetadataSchema>;

export const difficultySchema = z.union([
  difficultyNameSchema,
  difficultyMetadataSchema,
]);
export type Difficulty = z.infer<typeof difficultySchema>;

export const chartSchema = z.object({
  /** Optional song metadata for display purposes. */
  song: songMetadataSchema.optional(),
  /** Theme identifier for the chart. */
  theme: themeSchema.optional(),
  /** Difficulty information for this chart. */
  difficulty: difficultySchema,
  /** Hit objects comprising the chart. */
  hitObjects: z.array(noteEventSchema),
});
export type Chart = z.infer<typeof chartSchema>;

function formatZodError(err: ZodError): string {
  return err.errors
    .map((e) => `${e.path.join('.')}: ${e.message}`)
    .join('; ');
}

export function parseChart(json: unknown): Chart {
  try {
    return chartSchema.parse(json);
  } catch (err) {
    if (err instanceof ZodError) {
      throw new Error(`Invalid chart: ${formatZodError(err)}`);
    }
    throw err;
  }
}

export function parseNoteEvent(json: unknown): NoteEvent {
  try {
    return noteEventSchema.parse(json);
  } catch (err) {
    if (err instanceof ZodError) {
      throw new Error(`Invalid note event: ${formatZodError(err)}`);
    }
    throw err;
  }
}

/** Compute the time in milliseconds that an object becomes visible before it is hit. */
export function preemptMs(ar: number): number {
  return ar < 5 ? 1800 - 120 * ar : 1200 - 150 * (ar - 5);
}

/** Compute the fade-in duration in milliseconds based on approach rate. */
export function fadeInMs(ar: number): number {
  return preemptMs(ar) * 0.4;
}

/** Normalize a time in seconds to milliseconds relative to the audio start. */
export function normalizeTime(
  time: number,
  audioOffset = 0,
  chartOffset = 0,
): number {
  return (time + audioOffset + chartOffset) * 1000;
}

/** Parse a hit circle applying global and chart-specific timing offsets. */
export function parseHitCircle(
  note: CircleNote,
  ar: number,
  audio: Audio,
  difficulty?: Difficulty,
): HitCircle {
  const audioOffset = audio.offset ?? 0;
  const chartOffset =
    typeof difficulty === 'object' && 'offset' in difficulty
      ? difficulty.offset ?? 0
      : 0;
  return {
    tStart: normalizeTime(note.time, audioOffset, chartOffset),
    pos: { x: note.x, y: note.y },
    approachRate: ar,
  };
}

/** Raw slider representation used for parsing. */
export interface SliderInput extends CircleNote {
  endTime: number;
  /** Optional encoded path commands for the slider. */
  path?: string[];
  /** Pre-smoothed polyline points for the slider path. */
  points?: { x: number; y: number }[];
  /** Number of times the slider repeats. */
  repeats?: number;
  /** Slider length in pixels relative to the circle playfield. */
  pixelLength?: number;
}

/** Parse a slider applying global and chart-specific timing offsets. */
export function parseSlider(
  note: SliderInput,
  ar: number,
  audio: Audio,
  difficulty?: Difficulty,
): Slider {
  const audioOffset = audio.offset ?? 0;
  const chartOffset =
    typeof difficulty === 'object' && 'offset' in difficulty
      ? difficulty.offset ?? 0
      : 0;
  return {
    tStart: normalizeTime(note.time, audioOffset, chartOffset),
    tEnd: normalizeTime(note.endTime, audioOffset, chartOffset),
    pos: { x: note.x, y: note.y },
    approachRate: ar,
    path: note.path,
    points: note.points,
    repeats: note.repeats,
    pixelLength: note.pixelLength,
  };
}

/** Raw spinner representation used for parsing. */
export interface SpinnerInput {
  time: number;
  endTime: number;
  x: number;
  y: number;
}

/** Parse a spinner applying global and chart-specific timing offsets. */
export function parseSpinner(
  note: SpinnerInput,
  ar: number,
  audio: Audio,
  difficulty?: Difficulty,
): Spinner {
  const audioOffset = audio.offset ?? 0;
  const chartOffset =
    typeof difficulty === 'object' && 'offset' in difficulty
      ? difficulty.offset ?? 0
      : 0;
  return {
    tStart: normalizeTime(note.time, audioOffset, chartOffset),
    tEnd: normalizeTime(note.endTime, audioOffset, chartOffset),
    pos: { x: note.x, y: note.y },
    approachRate: ar,
  };
}
